/**
 * payslipBuilders.js
 * Shared row-builder utilities used by both PayslipModal (preview) and
 * payslipPdfService (PDF generation). Keep these in sync — they are the
 * single source of truth for what appears on every payslip.
 */

import { EARNING_CATEGORIES, DEDUCTION_CATEGORIES, LEGACY_CATEGORIES } from '../services/adjustmentService';
import { calculateEmployeeFinancials, allocateRoundingResidue, periodHours } from './payrollCalculations';
import { formatPeso } from './currency';

// An adjustment's payslip line: its description, else its category's label
// (Description is optional on both adjustment forms — PRD_SharedEmployeePicker C11).
const CATEGORY_LABEL = new Map(
  [...EARNING_CATEGORIES, ...DEDUCTION_CATEGORIES, ...LEGACY_CATEGORIES].map((c) => [c.value, c.label])
);
const adjustmentLabel = (adj) => adj.label || adj.description || CATEGORY_LABEL.get(adj.category) || 'Adjustment';

// PRD_PayrollFigureAlignment C2 — the deduction headings, in render order. Lives
// here, beside the builder that stamps `group`, so the modal and the PDF cannot
// drift apart and neither pulls the other in.
export const DEDUCTION_GROUPS = [
  { key: 'government', heading: 'Government Contributions' },
  { key: 'loans', heading: 'Loans' },
  { key: 'other', heading: 'Other Deductions' },
];

// Both renderers draw only the three groups above, so a row tagged with anything
// else — or left untagged by a future edit here — would be filtered out and the
// money would silently disappear from the payslip while Total Deductions still
// counted it. Fall everything unrecognised into the last group instead. (Same
// whitelist shape as DAY_TYPE_ORDER, which has dropped rows before.)
export const groupOf = (row) =>
  DEDUCTION_GROUPS.some((g) => g.key === row.group) ? row.group : 'other';

/** Returns all calendar-month keys (YYYY-MM) between two Date objects. */
export function getMonthsInPeriod(start, end) {
  const months = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= endMonth) {
    months.push(
      `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`
    );
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

/** Format YYYY-MM as "Jan 2026". */
export function formatMonthLabel(key) {
  const [y, m] = key.split('-');
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(
    new Date(Number(y), Number(m) - 1, 1)
  );
}

/** dayType → human label. Shared by the Overtime and Night Differential rows. */
export function otDayTypeLabel(dayType) {
  const map = {
    regular: 'Regular Day',
    restDay: 'Rest Day',
    regularHoliday: 'Regular Holiday',
    regularHolidayRestDay: 'Reg Holiday + Rest Day',
    specialHoliday: 'Special Holiday',
    specialHolidayRestDay: 'Spl Holiday + Rest Day',
    holiday: 'Regular Holiday', // legacy alias — engine now emits 'regularHoliday'
  };
  return map[dayType] || dayType;
}

/**
 * Render order for day-typed detail rows, shared by the Overtime and Night
 * Differential branches (BL-086 C12) — one ordering, two consumers.
 *
 * It is a WHITELIST, so both consumers must also append any dayType not listed
 * here rather than dropping it: an earlier list used 'holiday' and omitted the
 * *RestDay variants, which silently dropped those OT rows from payslips.
 */
export const DAY_TYPE_ORDER = [
  'regular', 'restDay', 'regularHoliday', 'regularHolidayRestDay',
  'specialHoliday', 'specialHolidayRestDay',
];

/** "1 day" / "2 days", "1 hr" / "3 hrs" — decided on the number as printed (formatNum). */
const unitFor = (quantity, singular, plural) => (formatNum(quantity) === 1 ? singular : plural);

/** Format a number to up to 2 decimal places, stripping trailing zeros. */
export function formatNum(val) {
  if (val == null) return null;
  return parseFloat(Number(val).toFixed(2));
}

/**
 * BL-093 — the base an OT / night-diff / undertime row was priced on, in words, when
 * it was the company minimum wage rather than the employee's own daily rate
 * (PRD_CbaPremiumBase). Without it the amount cannot be reconciled from the
 * employee's rate and reads as an error. Amendment A3: a base in words, never a
 * multiplier.
 *
 * Read from the run alone — `rates` is the run's `ratesUsed` snapshot and
 * `emp.employment` is the line as computed — never live config, so a reprint
 * shows what the run was computed on. Mirrors the engine's gate
 * (computeEarnings.js): employee switch AND company policy for that premium AND a
 * positive wage. Runs saved before PRD_CbaPremiumBase carry no `minimumDailyWage`
 * and get no note.
 *
 * KEEP IN STEP with computeEarnings.js (computeDailyEmployee's `cbaOn && bases.X`
 * gate): this re-states the engine's rule rather than reading what the engine used,
 * so a change to that rule must be made here too or payslips will describe a base
 * the run never priced on. Stamping the base on the line instead was considered and
 * declined (2026-10-13): it would change the pay engine and the saved line shape.
 *
 * Known and deliberately unhandled: salaryType is not checked. The engine reads the
 * flag only on the daily path (computeEarnings.js dispatches monthly staff to
 * computeMonthlyEmployee), and the wizard clears the flag when salary type becomes
 * monthly (StepSensitiveInfo.js), so a monthly line carrying it needs legacy or
 * hand-edited data. Such a line would show this note on a premium it did not price.
 *
 * @param {Object} emp
 * @param {Object} rates - run.ratesUsed
 * @param {'overtime'|'nightDiff'|'undertime'} premium - key in rates.cbaPremiumBases
 * @returns {?string}
 */
export function cbaBaseNote(emp, rates, premium) {
  if (emp.employment?.cbaPremiumBase !== true) return null;
  if (!rates?.cbaPremiumBases?.[premium]) return null;
  const wage = parseFloat(rates.minimumDailyWage) || 0;
  if (wage <= 0) return null;
  // Only when the base actually differs from the employee's own daily rate.
  const own = parseFloat(emp.employment?.dailySalary ?? emp.dailyRate) || 0;
  if (Math.abs(wage - own) < 0.005) return null;
  return `priced on minimum wage ${formatPeso(wage)}/day`;
}

const joinSubLabels = (...parts) => parts.filter(Boolean).join(' \u00b7 ') || null;

/** Sum a per-month deduction object or flat number. */
export function sumDeduction(d) {
  if (typeof d === 'object' && d !== null && !Array.isArray(d)) {
    return Object.values(d).reduce((s, v) => s + (v || 0), 0);
  }
  return d || 0;
}

/**
 * Build earnings rows for the Earnings table.
 * Returns [{ label, subLabel?, quantity?, unit?, amount, isNegative? }]
 *
 * @param {Object} emp   - Employee record from payrollRun.employees[]
 * @param {Object} rates - run.ratesUsed snapshot. Rows print no rate (Amendment
 *   A3); read only for the minimum-wage base note (BL-093, cbaBaseNote).
 */
export function buildEarningsRows(emp, rates) {
  const earnings = emp.earnings || {};
  const rows = [];
  const otNote = cbaBaseNote(emp, rates, 'overtime');
  const ndNote = cbaBaseNote(emp, rates, 'nightDiff');
  const utNote = cbaBaseNote(emp, rates, 'undertime');

  // PRD_PayrollFigureAlignment Amendment A3 — no row prints a rate multiplier. A
  // rest-day or worked-holiday row carries only the premium while its rate was the
  // whole day's (1.3× beside a 30% top-up), and on a CBA premium base the OT and
  // night-diff rates apply to a base the payslip never shows. A rate the employee
  // cannot reproduce from the page reads as an error, so rows show the day type,
  // the hours or days, and the amount.

  // Basic Pay — always shown. Monthly is deduct-down (PRD_MonthlySalaryType):
  // the base is a fixed half-month amount, not a per-day sum, so a "days worked"
  // count is meaningless — and misleading at 0 days (₱15,000 for "0 days" reads
  // like a daily employee earning nothing). Omit the day count for monthly.
  const isMonthlyBase = emp.employment?.salaryType === 'monthly';
  rows.push({
    label: 'Basic Pay',
    quantity: isMonthlyBase ? null : (emp.daysWorked ?? emp.earnings?.daysWorked ?? null),
    unit: isMonthlyBase ? null : unitFor(emp.daysWorked ?? emp.earnings?.daysWorked, 'day', 'days'),
    amount: earnings.basic || 0,
    alwaysShow: true,
  });

  // Overtime — consolidate by day type (one row per type, summed hours & pay)
  const otDetails = earnings.overtimeDetails || [];
  if (otDetails.length > 0) {
    const otByType = {};
    otDetails.forEach((ot) => {
      if ((ot.pay || 0) > 0) {
        const key = ot.dayType || 'regular';
        if (!otByType[key]) otByType[key] = { hours: 0, pay: 0 };
        otByType[key].hours += ot.hours || 0;
        otByType[key].pay += ot.pay || 0;
      }
    });
    // Render known day types in a sensible order, then ANY other dayType the
    // engine emits — never silently drop an OT row. (The old list used 'holiday'
    // and omitted the *RestDay variants, so the engine's 'regularHoliday' /
    // 'regularHolidayRestDay' / 'specialHolidayRestDay' OT was dropped.)
    const typeOrder = DAY_TYPE_ORDER;
    const orderedKeys = [
      ...typeOrder.filter((key) => otByType[key]),
      ...Object.keys(otByType).filter((key) => !typeOrder.includes(key)),
    ];
    orderedKeys.forEach((key) => {
      const { hours, pay } = otByType[key];
      // Named whenever it is not an ordinary day, like the Night Differential rows.
      const subLabel = joinSubLabels(key === 'regular' ? null : otDayTypeLabel(key), otNote);
      rows.push({ label: 'Overtime Pay', subLabel, quantity: hours, unit: unitFor(hours, 'hr', 'hrs'), amount: pay });
    });
  } else if ((earnings.overtime || 0) > 0) {
    rows.push({ label: 'Overtime Pay', ...(otNote && { subLabel: otNote }), amount: earnings.overtime });
  }

  // Holiday Pay — consolidate by type (Regular / Special)
  const hDetails = earnings.holidayDetails || [];
  if (hDetails.length > 0) {
    const hByType = {};
    hDetails.forEach((h) => {
      if ((h.holidayPay || 0) > 0) {
        const key = h.type === 'regular' ? 'regular' : 'special';
        if (!hByType[key]) hByType[key] = { amount: 0, days: 0 };
        hByType[key].amount += h.holidayPay;
        hByType[key].days += 1;
      }
    });
    if (hByType.regular) {
      rows.push({ label: 'Regular Holiday Pay', quantity: hByType.regular.days, unit: unitFor(hByType.regular.days, 'day', 'days'), amount: hByType.regular.amount });
    }
    if (hByType.special) {
      rows.push({ label: 'Special Holiday Pay', quantity: hByType.special.days, unit: unitFor(hByType.special.days, 'day', 'days'), amount: hByType.special.amount });
    }
  } else if ((earnings.holiday || 0) > 0) {
    rows.push({ label: 'Holiday Pay', amount: earnings.holiday });
  }

  // Rest Day Premium — earnings.restDay is the top-up only; the day's base pay is
  // inside Basic Pay (computeEarnings splits it), so "Pay" would invite adding
  // the day again.
  if ((earnings.restDay || 0) > 0) {
    const restDayCount = (earnings.restDayDetails || []).length || null;
    rows.push({ label: 'Rest Day Premium', quantity: restDayCount, unit: unitFor(restDayCount, 'day', 'days'), amount: earnings.restDay });
  }

  // Night Differential — split by day type, mirroring the Overtime row above.
  // BL-086 made ND a per-record, day-typed premium. Grouped by dayType so a
  // rest-day row and a special-holiday row stay separate lines; the day type is
  // named whenever it is not an ordinary day (Amendment A3: no rate is printed).
  const ndDetails = earnings.nightDiffDetails || [];
  if (ndDetails.length > 0) {
    const ndByType = {};
    ndDetails.forEach((nd) => {
      if ((nd.pay || 0) > 0) {
        const key = nd.dayType || 'regular';
        if (!ndByType[key]) ndByType[key] = { hours: 0, pay: 0, otHours: 0, otPay: 0 };
        ndByType[key].hours += nd.hours || 0;
        ndByType[key].pay += nd.pay || 0;
        ndByType[key].otHours += nd.ndOtHours || 0;
        ndByType[key].otPay += nd.ndOtPay || 0;
      }
    });
    const ndOrderedKeys = [
      ...DAY_TYPE_ORDER.filter((key) => ndByType[key]),
      ...Object.keys(ndByType).filter((key) => !DAY_TYPE_ORDER.includes(key)),
    ];
    ndOrderedKeys.forEach((key) => {
      const { hours, pay, otHours, otPay } = ndByType[key];
      const subLabel = joinSubLabels(key === 'regular' ? null : otDayTypeLabel(key), ndNote);
      rows.push({
        label: 'Night Differential', subLabel, quantity: hours, unit: unitFor(hours, 'hr', 'hrs'), amount: pay,
        // PRD_ShiftRules A3: split into its own row after the residue step below.
        ...(otHours > 0 && { ndOt: { hours: otHours, pay: otPay } }),
      });
    });
  } else if ((earnings.nightDiff || 0) > 0) {
    // Legacy run (saved before nightDiffDetails existed): one un-split row.
    const hours = periodHours(emp, 'totalNightDiffHours', 'nightDiffHours');
    rows.push({
      label: 'Night Differential',
      ...(ndNote && { subLabel: ndNote }),
      quantity: hours,
      unit: unitFor(hours, 'hr', 'hrs'),
      amount: earnings.nightDiff,
    });
  }

  // Piece Rate
  if ((emp.pieceRate || 0) > 0) {
    rows.push({ label: 'Piece Rate', amount: emp.pieceRate });
  }

  // PRD_PayrollFigureAlignment C1 — manual earning adjustments are NO LONGER folded
  // in here. They were, and because PayslipModal derived Gross by summing these
  // rows, Gross silently included them — which is why one run printed Gross
  // ₱1,890.63 on the individual payslip and ₱1,390.63 on the bulk one (BL-048).
  // Gross is now statutory earnings only, and the adjustments render in their own
  // Additional Earnings block via buildAdditionalEarnings() below (C4).

  // Late / Undertime / Absence (negative — reduce gross)
  const late = emp.deductions?.late || 0;
  const undertime = emp.deductions?.undertime || 0;
  const absence = emp.deductions?.absence || 0;
  if (late > 0) {
    rows.push({ label: 'Late', amount: -late, isNegative: true });
  }
  if (undertime > 0) {
    const utHours = periodHours(emp, 'totalUndertimeHours', 'undertimeHours');
    rows.push({
      label: 'Undertime',
      ...(utNote && { subLabel: utNote }),
      quantity: utHours,
      unit: unitFor(utHours, 'hr', 'hrs'),
      amount: -undertime,
      isNegative: true,
    });
  }
  // M1 deduct-down (PRD_MonthlySalaryType): confirmed unpaid days for
  // monthly-paid employees — "Basic ₱15,000 · less Absences −₱986…".
  if (absence > 0) {
    rows.push({
      label: 'Absences',
      quantity: emp.absenceDetails?.length ?? null,
      unit: emp.absenceDetails?.length === 1 ? 'day' : 'days',
      amount: -absence,
      isNegative: true,
    });
  }

  // PRD_PayrollFigureAlignment C6b — the printed rows must add to the printed Gross.
  // Row amounts are raw engine values (OT/ND/undertime are persisted unrounded), so
  // rounding each for display can leave the column a centavo away from Gross Pay —
  // the exact "it doesn't add up" the customer reported, one screen over. Same
  // helper, same rule, as buildPayComposition.
  const gross = calculateEmployeeFinancials(emp).gross;
  const cents = allocateRoundingResidue(rows.map((r) => r.amount), Math.round(gross * 100));

  // PRD_ShiftRules A3 — a group's night hours that are also OT print as their own
  // row, split AFTER the residue step so the pair adds to the group's printed figure
  // and every other row is unchanged. The plain row is dropped when every night hour
  // in the group is OT.
  return rows.flatMap(({ ndOt, ...row }, i) => {
    row.amount = cents[i] / 100;
    if (!ndOt) return [row];
    const otCents = Math.round(ndOt.pay * 100);
    const otRow = {
      ...row, label: 'Night Differential on Overtime', quantity: ndOt.hours,
      unit: unitFor(ndOt.hours, 'hr', 'hrs'), amount: otCents / 100,
    };
    const plainHours = Math.round((row.quantity - ndOt.hours) * 100) / 100;
    if (!(plainHours > 0)) return [otRow];
    return [
      { ...row, quantity: plainHours, unit: unitFor(plainHours, 'hr', 'hrs'), amount: (cents[i] - otCents) / 100 },
      otRow,
    ];
  });
}

/**
 * Build the Additional Earnings rows — manual earning adjustments only.
 *
 * PRD_PayrollFigureAlignment C4: these sit OUTSIDE Gross (an administrative act
 * after the fact is not something an employee can verify from their DTR) and
 * render between Total Deductions and Net Pay. C12: the block is hidden entirely
 * when the total is zero, which is most payslips.
 *
 * @param {Object} emp - Employee record from payrollRun.employees[]
 * @returns {Object} { rows: [{ label, amount }], total }
 */
export function buildAdditionalEarnings(emp) {
  const rows = (emp.earnings?.adjustments || [])
    .filter((adj) => (adj.amount || 0) !== 0)
    .map((adj) => ({ label: adjustmentLabel(adj), amount: adj.amount }));
  return { rows, total: rows.reduce((sum, r) => sum + r.amount, 0) };
}

/**
 * Build deductions rows for the Deductions table.
 * Reads from employee.deductions.{sss/philhealth/pagibig/tax} per-month maps.
 * Returns [{ label, thisPeriod }]
 *
 * @param {Object} emp         - Employee record
 * @param {Date}   periodStart - Pay period start date
 * @param {Date}   periodEnd   - Pay period end date
 */
export function buildDeductionRows(emp, periodStart, periodEnd) {
  const deductions = emp.deductions || {};
  const months = getMonthsInPeriod(periodStart, periodEnd);
  const multiMonth = months.length > 1;

  const getMonthVal = (map, month) => {
    if (typeof map === 'object' && map !== null && !Array.isArray(map)) {
      return map[month] || 0;
    }
    return !multiMonth ? (map || 0) : 0;
  };

  const rows = [];

  // PRD_PhilHealthContributionBasis H7 generalises what C24 built for SSS alone.
  // Any agency whose basis composed is PULLED OUT of the per-month walk: its
  // deduction is filed under the APPLICABLE month, never a period month, so the
  // walk would read it as ₱0 and drop it from the total. Gated on the run's OWN
  // persisted *BasisResult (this builder receives no settings), so absent means
  // that agency's basis was off for this run.
  //
  // Pulled rows come FIRST, in canonical SSS → PhilHealth order, each labelled
  // with its month and summed month-agnostically (a stale key left at 0 by the
  // step's cleanup contributes nothing); omitted when zero — a deliberate
  // divergence from the single-month branch, which prints ₱0.00 rows.
  //
  // Generalised rather than special-cased twice: two hard-coded exemptions is
  // the point at which the all-zero skip below gets the third one wrong.
  const PULLABLE = [
    { key: 'sss', label: 'SSS', result: emp.sssBasisResult },
    { key: 'philhealth', label: 'PhilHealth', result: emp.philhealthBasisResult },
  ];
  const pulled = PULLABLE
    .map((a) => ({ ...a, month: a.result && a.result.applicableMonth ? a.result.applicableMonth : null }))
    .filter((a) => a.month);
  const isPulled = (key) => pulled.some((a) => a.key === key);

  pulled.forEach((a) => {
    const amount = sumDeduction(deductions[a.key]);
    if (amount !== 0) {
      rows.push({ label: `${a.label} (${formatMonthLabel(a.month)})`, thisPeriod: amount, group: 'government' });
    }
  });

  if (multiMonth) {
    months.forEach((month) => {
      // A pulled agency contributes 0 here — it was already rendered above —
      // and must still count toward the all-zero skip as 0, not as its real
      // value. Otherwise a month holding only a pulled agency's deduction
      // prints the rows that are NOT suppressed by the `!isPulled` guards
      // below: three with one agency pulled, two with both.
      const sss = isPulled('sss') ? 0 : getMonthVal(deductions.sss, month);
      const ph  = isPulled('philhealth') ? 0 : getMonthVal(deductions.philhealth, month);
      const pi  = getMonthVal(deductions.pagibig, month);
      const tax = getMonthVal(deductions.tax, month);
      if (sss + ph + pi + tax === 0) return;
      const suffix = ` (${formatMonthLabel(month)})`;
      if (!isPulled('sss')) rows.push({ label: `SSS${suffix}`, thisPeriod: sss, group: 'government' });
      if (!isPulled('philhealth')) rows.push({ label: `PhilHealth${suffix}`, thisPeriod: ph, group: 'government' });
      rows.push({ label: `Pag-IBIG${suffix}`,        thisPeriod: pi,  group: 'government' });
      rows.push({ label: `Withholding Tax${suffix}`, thisPeriod: tax, group: 'government' });
    });
  } else {
    if (!isPulled('sss')) rows.push({ label: 'SSS', thisPeriod: sumDeduction(deductions.sss), group: 'government' });
    if (!isPulled('philhealth')) rows.push({ label: 'PhilHealth', thisPeriod: sumDeduction(deductions.philhealth), group: 'government' });
    rows.push({ label: 'Pag-IBIG',        thisPeriod: sumDeduction(deductions.pagibig),    group: 'government' });
    rows.push({ label: 'Withholding Tax', thisPeriod: sumDeduction(deductions.tax),        group: 'government' });
  }

  // PRD_LoansManagement C8a — loan rows AFTER the four statutory rows and BEFORE the
  // adjustments, labelled with the loan name and, in a multi-month period, suffixed
  // with the month exactly as the statutory rows are (C14m). A line without the
  // bucket (a historical run) renders byte-identically to before.
  // PRD_PayrollFigureAlignment C2 — every row now carries its `group`, which is
  // what closes BL-088: a "Cash advance" rendered in the same flat list as SSS
  // reads as a government contribution, contradicting the wizard screen the admin
  // just approved. The renderers group on this field; the ORDER is unchanged, so a
  // renderer that ignores `group` is byte-identical to before.
  (Array.isArray(deductions.loans) ? deductions.loans : []).forEach((loan) => {
    if ((loan.amount || 0) !== 0) {
      const name = loan.name || 'Loan';
      const label = multiMonth && loan.monthKey ? `${name} (${formatMonthLabel(loan.monthKey)})` : name;
      rows.push({ label, thisPeriod: loan.amount, group: 'loans' });
    }
  });

  (deductions.adjustments || []).forEach((adj) => {
    if ((adj.amount || 0) !== 0) {
      rows.push({ label: adjustmentLabel(adj), thisPeriod: adj.amount, group: 'other' });
    }
  });

  return rows;
}

/** Resolve employee display name from available fields. */
export function getPayslipEmployeeName(emp) {
  return (
    emp.employeeName ||
    emp.personalInfo?.fullName ||
    `${emp.personalInfo?.firstName || ''} ${emp.personalInfo?.lastName || ''}`.trim() ||
    'Unknown'
  );
}
